import { useState, useRef, useEffect, useMemo } from "react";
import { isNullish } from "@theorgchartdev/storybook";
import { useTableManagerContext } from "@/providers/AppendTableProvider";
import { LookupRecord } from "@/Utils/Promises";
import { appstr } from "@coreapp/utils/appstr";
import { FormatEmployeeDictLabel } from "@coreapp/utils/FormatEmployeeDictLabel";

export const useAdditionalReports = (chartConfig, record, getValue) => {
  const [reportsData, setReportsData] = useState([]);
  const [validation, setValidation] = useState({ isValid: true, status: "default", message: "" });
  const [isDirty, setIsDirty] = useState(false);
  const [auxSuperFields, setAuxSuperFields] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const initialReportsRef = useRef(null);

  const { tableManager } = useTableManagerContext();

  const externalReportIds = useMemo(() => {
    return record ? [record.SmartGetFieldValue("SupervisorID")] : [];
  }, [record]);

  useEffect(() => {
    if (initialReportsRef.current === null) {
      initialReportsRef.current = JSON.stringify({});
    }
  }, []);

  useEffect(() => {
    if (initialReportsRef.current) {
      const currentAuxSupervisors = {};

      reportsData.forEach(data => {
        if (data.reportId) {
          currentAuxSupervisors[data.fieldName] = data.reportId;
        }
      });

      const initialState = JSON.parse(initialReportsRef.current);
      const hasChanges = JSON.stringify(currentAuxSupervisors) !== JSON.stringify(initialState);
      setIsDirty(hasChanges);
    }
  }, [reportsData]);

  useEffect(() => {
    if (isNullish(record)) return;
    const { AuxChanges, auxMappingChanges } = getReportChanges();
    if (
      AuxChanges.length > 0 ||
      auxMappingChanges.added.length > 0 ||
      auxMappingChanges.removed.length > 0
    ) {
      return;
    }
    syncReports();
  }, [chartConfig, record]);

  useEffect(() => {
    handleReportValidation();
  }, [reportsData]);

  const getReportChanges = () => {
    if (isLoading) {
      return { AuxChanges: {}, auxMappingChanges: { added: [], removed: [] } };
    }

    const initialState = JSON.parse(initialReportsRef.current || "{}");
    const currentState = {};

    const auxMappingChanges = {
      added: [],
      removed: []
    };

    reportsData.forEach(data => {
      if (data.fieldName) {
        currentState[data.fieldName] = data.reportId || "";

        if (!initialState.hasOwnProperty(data.fieldName)) {
          auxMappingChanges.added.push({
            fieldId: data.fieldId,
            fieldName: data.fieldName
          });
        }
      }
    });

    const initialFieldNames = new Set(Object.keys(initialState));
    const currentFieldNames = new Set(Object.keys(currentState));

    const originalChartConfig = chartConfig?.fields || {};

    initialFieldNames.forEach(fieldName => {
      if (!currentFieldNames.has(fieldName)) {
        // Look up the fieldId from the original chart config
        const fieldId = Object.entries(originalChartConfig).find(
          ([key, value]) =>
            value === fieldName && (key === "AuxSuperID" || key.startsWith("AuxSuperID-"))
        )?.[0];

        if (fieldId) {
          auxMappingChanges.removed.push({
            fieldId,
            fieldName
          });
        }
      }
    });

    const AuxChanges = {};

    Object.keys(currentState).forEach(fieldName => {
      const initialValue = initialState[fieldName] || "";
      const currentValue = currentState[fieldName] || "";

      if (initialValue !== currentValue) {
        AuxChanges[fieldName] = currentValue;
      }
    });

    Object.keys(initialState).forEach(fieldName => {
      if (!currentState.hasOwnProperty(fieldName)) {
        AuxChanges[fieldName] = "";
      }
    });

    return {
      AuxChanges,
      auxMappingChanges
    };
  };

  const checkIfReportInUse = (reportData, currentReportId, existingReportIds = []) => {
    if (!currentReportId) return false;

    const duplicateCount = reportData.filter(
      item => item.reportId === currentReportId && item.reportId !== ""
    ).length;

    const existsInExternal = existingReportIds.includes(currentReportId);
    return duplicateCount > 1 || existsInExternal;
  };

  const syncReports = async () => {
    setIsLoading(true);
    const fields = chartConfig?.fields || {};

    const auxSuperIDFields = [];
    Object.entries(fields).forEach(([key, value]) => {
      if (key === "AuxSuperID" || key.startsWith("AuxSuperID-")) {
        auxSuperIDFields.push({ fieldId: key, fieldName: value });
      }
    });

    auxSuperIDFields.sort((a, b) => {
      const aNum = a.fieldId === "AuxSuperID" ? 0 : parseInt(a.fieldId.replace("AuxSuperID-", ""));
      const bNum = b.fieldId === "AuxSuperID" ? 0 : parseInt(b.fieldId.replace("AuxSuperID-", ""));
      return aNum - bNum;
    });

    setAuxSuperFields(auxSuperIDFields);

    const nextReportsData = [];
    const initialState = {};
    const reportIds = [];

    // First pass to collect all reportIds
    auxSuperIDFields.forEach(field => {
      const fieldName = field.fieldName;
      const reportToId = getValue(fieldName) || "";

      if (!isNullish(reportToId)) {
        reportIds.push(reportToId);
      }
    });

    // Fetch supervisor records for all reportIds at once
    // Filter out blank reportIds
    const validReportIds = reportIds.filter(id => id !== "");

    // Fetch supervisor records for all valid reportIds at once
    const supervisorRecords =
      validReportIds.length > 0 ? await LookupRecord(validReportIds, chartConfig?.ID) : [];

    const personKey = chartConfig["fields"]["PersonID"];

    auxSuperIDFields.forEach(field => {
      const fieldId = field.fieldId;
      const fieldName = field.fieldName;
      const reportToId = getValue(fieldName) || "";
      initialState[fieldName] = reportToId;

      let supervisorData = {};
      if (reportToId.length > 0) {
        supervisorData.id = reportToId;
        // Get name from the fetched supervisor records
        const supervisorRecord = supervisorRecords.find(
          supervisor => supervisor[personKey] === reportToId
        );
        supervisorData.name =
          FormatEmployeeDictLabel(supervisorRecord, chartConfig.fields) || reportToId;
      }

      nextReportsData.push({
        fieldId,
        fieldName,
        reportId: supervisorData.id || "",
        reportName: supervisorData.name || "",
        placeholder: field.fieldName,
        isBlank: !supervisorData.id,
        isInUse: false
      });
    });

    initialReportsRef.current = JSON.stringify(initialState);

    nextReportsData.forEach(data => {
      data.isInUse = checkIfReportInUse(nextReportsData, data.reportId, externalReportIds);
    });

    setReportsData(nextReportsData);
    setIsLoading(false);
  };
  const handleAddAuxSupervisor = () => {
    const hasBaseAuxSuperID = auxSuperFields.some(field => field.fieldId === "AuxSuperID");

    if (!hasBaseAuxSuperID) {
      const newFieldName = "Additional Report";
      setReportsData(prev => [
        ...prev,
        {
          fieldId: "AuxSuperID",
          fieldName: newFieldName,
          reportId: "",
          reportName: "",
          placeholder: newFieldName,
          isBlank: true,
          isInUse: false
        }
      ]);
      setAuxSuperFields(prev => [...prev, { fieldId: "AuxSuperID", fieldName: newFieldName }]);
      return;
    }

    let nextIndex = 1;

    auxSuperFields.forEach(field => {
      if (field.fieldId.startsWith("AuxSuperID-")) {
        const index = parseInt(field.fieldId.replace("AuxSuperID-", ""));
        if (index >= nextIndex) {
          nextIndex = index + 1;
        }
      }
    });

    const newFieldId = `AuxSuperID-${nextIndex}`;
    const newFieldName = `Additional Report ${nextIndex}`;

    setReportsData(prev => [
      ...prev,
      {
        fieldId: newFieldId,
        fieldName: newFieldName,
        reportId: "",
        reportName: "",
        placeholder: newFieldName,
        isBlank: true,
        isInUse: false
      }
    ]);

    setAuxSuperFields(prev => [...prev, { fieldId: newFieldId, fieldName: newFieldName }]);
  };

  const handleSearchSelect = (idx, selectedItem) => {
    if (!selectedItem) return;

    const record = selectedItem.result;
    const reportId = selectedItem.value;
    const reportName = FormatEmployeeDictLabel(record, chartConfig.fields) || selectedItem.label;

    setReportsData(prev => {
      const updatedReports = prev.map((data, i) =>
        i === idx ? { ...data, reportId, reportName, isBlank: false } : data
      );

      return updatedReports.map(data => ({
        ...data,
        isInUse: checkIfReportInUse(updatedReports, data.reportId, externalReportIds)
      }));
    });
  };

  const handleClearReport = idx => {
    setReportsData(prev => {
      const updatedReports = prev.map((data, i) =>
        i === idx ? { ...data, reportId: "", reportName: "", isBlank: true } : data
      );

      return updatedReports.map(data => ({
        ...data,
        isInUse: checkIfReportInUse(updatedReports, data.reportId, externalReportIds)
      }));
    });
  };

  const handleRemove = reportIdx => {
    setReportsData(prev => prev.filter((_, idx) => idx !== reportIdx));
    setAuxSuperFields(prev =>
      prev.filter(field => field.fieldId !== reportsData[reportIdx]?.fieldId)
    );
  };

  const handleReportValidation = () => {
    const isDuplicateReport = reportsData.find(r => r.isInUse);
    if (isDuplicateReport) {
      setValidation({
        isValid: false,
        message: appstr("Duplicate additional reports are not allowed"),
        status: "error"
      });
      return;
    }

    setValidation({ isValid: true, status: "default", message: "" });
  };

  const getTableFieldChanges = () => {
    const { auxMappingChanges } = getReportChanges();
    if (auxMappingChanges.removed.length === 0 && auxMappingChanges.added.length === 0) {
      return false;
    }

    const tableMgr = tableManager.state;
    const tableInfo = tableMgr.fTableInfo;
    const nextTable = { ...tableInfo };
    const additionalFields = nextTable.additionalFields || [];
    let deletedFields = [];

    auxMappingChanges.removed.forEach(field => {
      const fieldIndex = additionalFields.indexOf(field.fieldName);
      if (fieldIndex !== -1) {
        additionalFields.splice(fieldIndex, 1);
        deletedFields.push(field.fieldName); // Track deleted fields
      }
    });

    auxMappingChanges.added.forEach(field => {
      if (!additionalFields.includes(field.fieldName)) {
        additionalFields.push(field.fieldName);
      }
    });

    return { additionalFields, deletedFields };
  };

  const getChartConfigChanges = () => {
    const { auxMappingChanges } = getReportChanges();
    if (auxMappingChanges.removed.length === 0 && auxMappingChanges.added.length === 0) {
      return false;
    }

    const updatedChartConfig = { ...chartConfig };
    const newMapping = { ...updatedChartConfig.fields };
    const newHeaders = [...updatedChartConfig.headers];

    auxMappingChanges.removed.forEach(field => {
      if (newMapping[field.fieldId]) {
        delete newMapping[field.fieldId];

        const headerIndex = newHeaders.indexOf(field.fieldName);
        if (headerIndex !== -1) {
          newHeaders.splice(headerIndex, 1);
        }
      }
    });

    auxMappingChanges.added.forEach(field => {
      newMapping[field.fieldId] = field.fieldName;

      if (!newHeaders.includes(field.fieldName)) {
        newHeaders.push(field.fieldName);
      }
    });

    return { fields: newMapping, headers: newHeaders };
  };

  const isValidReports = () => {
    handleReportValidation();
    return validation.isValid;
  };

  const reset = async () => {
    setReportsData([]);
    setIsDirty(false);
    setValidation({ isValid: true, status: "default", message: "" });
    await syncReports();
  };

  return {
    reportsData,
    setReportsData,
    validation,
    isDirty,
    auxSuperFields,
    setAuxSuperFields,
    initialReportsRef,
    handleAddAuxSupervisor,
    handleSearchSelect,
    handleClearReport,
    handleRemove,
    getChartConfigChanges,
    getTableFieldChanges,
    getReportChanges,
    isValidReports,
    reset,
    setIsDirty
  };
};
